import React from 'react';
import { DocumentDuplicateIcon } from '@heroicons/react/24/outline';
import { HChapter, HMetaphor, HSection, HParagraph, HStep, HTip, HBold, HWarning } from '../HandbookContent';

export default function ChapterCloning() {
  return (
    <HChapter
      title="Cloning & Customizing"
      subtitle="How to create your own version of a playbook and tailor it to your firm."
    >
      <HMetaphor
        icon={<DocumentDuplicateIcon className="w-5 h-5 text-lex-accent" />}
        title="Making It Yours"
      >
        Think of cloning like photocopying a form and then writing in your own details. The original
        stays untouched in the filing cabinet, and you get a fresh copy to mark up, customize, and
        make your own. Your version lives alongside the original, and you can always compare back to
        see what you've changed.
      </HMetaphor>

      <HSection title="Cloning a System Playbook">
        <HStep number={1} title="Find the system playbook in the Library">
          Navigate to the Playbook Library and locate the system playbook you want to customize.
          System playbooks have a "System" badge.
        </HStep>
        <HStep number={2} title="Click the 'Clone' button">
          On the playbook card, click the Clone button. The system creates a new copy with the name
          "[Original Name] — Firm Copy" and sets it to Draft status.
        </HStep>
        <HStep number={3} title="Open and customize your copy">
          Click into your new firm playbook. Use the Visual Editor to add, remove, or modify
          statuses, transitions, guards, validators, and actions.
        </HStep>
        <HStep number={4} title="Publish when ready">
          Once you're satisfied with your changes, publish the playbook to make it available for use.
        </HStep>
      </HSection>

      <HSection title="Tracking Upstream Changes">
        <HParagraph>
          When EdgeLex updates a system playbook (adding new guards, fixing workflows, or incorporating
          regulatory changes), your cloned version doesn't change automatically. Instead, you'll see
          an <HBold>"Upstream changes available"</HBold> badge on your firm playbook.
        </HParagraph>
        <HParagraph>
          Click the badge to open the diff view, which shows exactly what changed in the system version.
          You can then decide whether to incorporate those changes into your copy.
        </HParagraph>
      </HSection>

      <HSection title="Version Diff">
        <HParagraph>
          The <HBold>Show Changes</HBold> button in the Visual Editor lets you compare your current
          playbook against a previous version or the system original. Added items appear in green,
          removed items in red, and modified items in amber.
        </HParagraph>
      </HSection>

      <HWarning>
        Once you publish a playbook, existing matters using it will be governed by the new version.
        Always test your changes using the "Test Playbook" button before publishing to production.
      </HWarning>

      <HTip>
        Name your firm playbooks clearly. Instead of "Family Law — Firm Copy," use something
        descriptive like "Family Law Dissolution — CA (Our Process)." It makes it much easier to
        find later.
      </HTip>
    </HChapter>
  );
}
